import { useEffect, useMemo, useRef, useState } from 'react';
import industriesJson from '../../data/industries.json';
import { cashFlowCapacity, type CashFlowResult, type DealType, type YearFigures } from '../../../calculator/src/cashflow.js';
import { estimate7a, type Loan7aEstimate } from '../../../calculator/src/loan7a.js';
import { USE_LABELS, type UseOfProceeds } from '../../../calculator/src/maturity.js';
import { fmtMoney, parseAmount } from '../../intake/parseAnswer.js';
import { ACCEPT, ingest } from '../ingest.js';
import { fact } from '../../workspace/schema.js';
import { candidatesToGrid, type Grid, type YearRow } from '../../documents/toYears.js';
import { PageHead } from '../components.js';
import { useWorkspace } from '../state.js';
import { ChatPanel, type CalcSummary } from '../ChatPanel.js';

interface Industry {
  id: string; label: string; naics2: string; dealType?: DealType; referenceOnly: boolean; source: string;
  benchmarks: { cogsPct: number; opexPct: number; ebitdaPct: number };
}
const INDUSTRIES = (industriesJson as { industries: Industry[] }).industries;

/** Industries a lender would group the business with; the two general templates come last. */
const SECTORS: { value: string; label: string }[] = [
  { value: '72', label: 'Food service / lodging' }, { value: '44', label: 'Retail' }, { value: '62', label: 'Health care' },
  { value: '54', label: 'Professional services' }, { value: '23', label: 'Construction' }, { value: '81', label: 'Personal services / repair' },
  { value: '31', label: 'Manufacturing' }, { value: '48', label: 'Transportation' }, { value: '53', label: 'Real estate' },
  { value: '52', label: 'Finance / insurance' }, { value: '56', label: 'Admin & support' }, { value: '71', label: 'Arts & recreation' },
  { value: '61', label: 'Education' }, { value: '51', label: 'Information / software' }, { value: '42', label: 'Wholesale' },
];

const ROWS: { key: YearRow; label: string; hint: string }[] = [
  { key: 'revenue', label: 'Revenue (gross receipts)', hint: 'Line 1a' },
  { key: 'cogs', label: 'Cost of goods sold', hint: 'Line 2' },
  { key: 'operatingExpenses', label: 'Total operating expenses', hint: 'Total deductions' },
  { key: 'netIncome', label: 'Net income (taxable / ordinary)', hint: 'Required' },
  { key: 'ownerSalary', label: "Owner's / officers' pay", hint: 'Officer compensation' },
  { key: 'depreciation', label: 'Depreciation & amortization', hint: 'Add-back' },
  { key: 'interest', label: 'Interest expense', hint: 'Add-back' },
  { key: 'oneTimeItems', label: 'One-time items (+expense / −income)', hint: 'Optional' },
];


const THIS_YEAR = new Date().getFullYear();
const COLS = [String(THIS_YEAR - 3), String(THIS_YEAR - 2), String(THIS_YEAR - 1), 'YTD'];

interface Draft {
  industryId: string; sector: string; dealType: DealType; use: UseOfProceeds; state: string;
  grid: Grid; ytdMonths: number; projectedRevenue: string;
  requested: string; equity: string; dscr: number; rate: string; term: string;
  addBackSalary: boolean; replacementSalary: string; outsideIncome: string; existingDebt: string;
}

const DEFAULT: Draft = {
  industryId: '', sector: '', dealType: 'existing', use: 'working-capital', state: '',
  grid: {}, ytdMonths: 6, projectedRevenue: '', requested: '', equity: '', dscr: 1.25, rate: '', term: '',
  addBackSalary: false, replacementSalary: '', outsideIncome: '', existingDebt: '',
};

const KEY = 'tb-calculator';
function loadDraft(): Draft {
  try { const s = localStorage.getItem(KEY); return s ? { ...DEFAULT, ...JSON.parse(s) as Partial<Draft> } : DEFAULT; } catch { return DEFAULT; }
}

const amt = (s: string | undefined) => (s && s.trim() ? parseAmount(s)?.value : undefined);

interface Lender { name: string; city: string; hq: string; loans: number; medianAmount: number; minAmount: number; maxAmount: number; medianRatePct: number | null }
type LenderData = { asOf: string; states: Record<string, Record<string, Lender[]>> };

let lenderCache: Promise<LenderData> | null = null;
function loadLenders(): Promise<LenderData> {
  lenderCache ??= fetch('/data/lenders.json').then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json() as Promise<LenderData>; });
  return lenderCache;
}

export function matchLenders(data: LenderData, state: string, sector: string, amount: number): Lender[] {
  const byState = data.states[state];
  if (!byState) return [];
  const fits = (l: Lender) => amount <= 0 || (amount >= l.minAmount * 0.5 && amount <= l.maxAmount);
  const pick = [...(byState[sector] ?? []).filter(fits), ...(byState['*'] ?? []).filter(fits)];
  const seen = new Set<string>();
  return pick.filter((l) => !seen.has(l.name) && seen.add(l.name)).slice(0, 5);
}

export function Calculator() {
  const { update } = useWorkspace();
  const [d, setD] = useState<Draft>(loadDraft);
  const [lenders, setLenders] = useState<LenderData | null>(null);
  const [fileMsg, setFileMsg] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((cur) => ({ ...cur, [k]: v }));

  useEffect(() => { try { localStorage.setItem(KEY, JSON.stringify(d)); } catch { /* convenience only */ } }, [d]);
  useEffect(() => { loadLenders().then(setLenders).catch(() => setLenders(null)); }, []);

  const industry = INDUSTRIES.find((i) => i.id === d.industryId);
  const sector = industry?.naics2 || d.sector;

  const pickIndustry = (id: string) => {
    const ind = INDUSTRIES.find((i) => i.id === id);
    setD((cur) => ({
      ...cur, industryId: id,
      ...(ind?.dealType ? { dealType: ind.dealType, addBackSalary: ind.dealType === 'acquisition' } : {}),
      ...(ind?.dealType === 'acquisition' ? { use: 'business-acquisition' as UseOfProceeds } : {}),
    }));
  };

  const years: YearFigures[] = useMemo(() => COLS.flatMap((col) => {
    const row = d.grid[col] ?? {};
    const netIncome = amt(row.netIncome);
    if (netIncome === undefined) return [];
    const y: YearFigures = { label: col === 'YTD' ? `${THIS_YEAR} YTD` : col, netIncome, ...(col === 'YTD' ? { months: d.ytdMonths } : {}) };
    for (const r of ROWS) if (r.key !== 'netIncome') { const v = amt(row[r.key]); if (v !== undefined) (y as unknown as Record<string, number>)[r.key] = v; }
    return [y];
  }), [d.grid, d.ytdMonths]);

  const requested = amt(d.requested) ?? 0;
  const equity = amt(d.equity) ?? 0;

  // Rate and term from the 7(a) engine; sized on the requested loan, or a typical one until we know.
  const loan = useMemo((): Loan7aEstimate | null => {
    try {
      const amount = requested > 0 ? requested : 350_000;
      return estimate7a({
        allocations: [{ use: d.use, amount: amount + equity }], equityInjection: equity,
        isChangeOfOwnership: d.dealType === 'acquisition',
        ...(sector ? { naicsSector: sector } : {}),
        ...(amt(d.rate) !== undefined ? { ratePct: amt(d.rate)! } : {}),
        ...(amt(d.term) ? { termMonths: amt(d.term)! * 12 } : {}),
      });
    } catch { return null; }
  }, [requested, equity, d.use, d.dealType, d.rate, d.term, sector]);

  const result = useMemo((): { r: CashFlowResult | null; error: string | null } => {
    if (!loan) return { r: null, error: 'Check the rate and term — they are outside what SBA allows for this use of funds.' };
    const startup = d.dealType === 'startup';
    if (!startup && years.length === 0) return { r: null, error: null };
    try {
      return {
        r: cashFlowCapacity({
          dealType: d.dealType, years: startup ? [] : years,
          ...(startup && amt(d.projectedRevenue) ? { projectedRevenue: amt(d.projectedRevenue)! } : {}),
          ...(industry && !(startup && industry.referenceOnly) ? { benchmark: industry.benchmarks } : {}),
          addBackOwnerSalary: d.addBackSalary,
          ...(amt(d.replacementSalary) ? { replacementSalary: amt(d.replacementSalary)! } : {}),
          ...(amt(d.outsideIncome) ? { outsideIncome: amt(d.outsideIncome)! } : {}),
          ...(amt(d.existingDebt) ? { existingDebtService: amt(d.existingDebt)! } : {}),
          dscrTarget: d.dscr, ratePct: loan.rate.pct, termMonths: loan.termMonths,
          ...(requested > 0 ? { requestedLoan: requested } : {}),
        }), error: null,
      };
    } catch (e) {
      return { r: null, error: e instanceof Error ? e.message : String(e) };
    }
  }, [loan, years, d, industry, requested]);
  const r = result.r;

  // Keep the shared workspace in step so Estimate, Scenarios and the action plan see these figures.
  useEffect(() => {
    const latest = years.filter((y) => !y.months).at(-1);
    update((ws) => ({
      ...ws, profile: {
        ...ws.profile,
        ...(sector ? { industry: fact(sector) } : {}),
        ...(requested > 0 ? { amount: fact(requested) } : {}),
        ...(latest?.revenue !== undefined ? { annualRevenue: fact(latest.revenue) } : {}),
        ...(latest ? { netIncome: fact(latest.netIncome) } : {}),
        ...(latest?.depreciation !== undefined ? { depreciation: fact(latest.depreciation) } : {}),
        ...(latest?.interest !== undefined ? { interestExpense: fact(latest.interest) } : {}),
        ...(latest?.ownerSalary !== undefined ? { ownerSalary: fact(latest.ownerSalary) } : {}),
      },
    }));
  }, [years, sector, requested, update]);

  const sizedAmount = requested > 0 ? requested : r?.maxLoan ?? 0;
  const matches = lenders && d.state && sector ? matchLenders(lenders, d.state, sector, sizedAmount) : [];

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    let filled = 0;
    let grid: Grid = d.grid;
    for (const file of Array.from(files)) {
      const rec = await ingest(file, 'tax-return');
      if (rec.status !== 'ready') { setFileMsg(`${file.name}: ${rec.statusDetail ?? 'could not be read'}`); continue; }
      const res = candidatesToGrid(grid, rec.candidates, file.name, COLS, THIS_YEAR);
      grid = res.grid; filled += res.filled;
    }
    set('grid', grid);
    if (filled) setFileMsg(`Filled ${filled} figure${filled === 1 ? '' : 's'} from your file. Check each one against the return.`);
    else setFileMsg((m) => m ?? 'No figures were recognised. Type them in from the return.');
  };

  const summary: CalcSummary = {
    industry: industry?.label ?? SECTORS.find((s) => s.value === sector)?.label ?? 'not chosen',
    naics2: sector, state: d.state, dealType: d.dealType, useOfFunds: USE_LABELS[d.use],
    requestedLoan: requested || null, equity: equity || null, dscrTarget: d.dscr,
    ratePct: loan?.rate.pct ?? null, termYears: loan?.termYears ?? null,
    maxLoan: r?.maxLoan ?? null, growthMaxLoan: r?.growthMaxLoan ?? null, limitingYear: r?.limitingYear ?? null,
    verdict: r?.verdict ?? null, minDscr: r?.requestedMinDscr ?? null,
    years: r?.years.map((y) => ({ label: y.label, cashFlow: y.availableForNewDebt, dscr: y.dscr })) ?? [],
    flags: r?.benchmarkFlags.map((f) => f.note) ?? [],
    sbaMinimum: r?.sbaRule ? { rule: r.sbaRule.label, minDscr: r.sbaRule.minDscr, basis: r.sbaRule.basis, maxLoan: r.sbaRule.maxLoan, requestedDscr: r.sbaRule.requestedDscr, meets: r.sbaRule.meets } : null,
  };

  const verdictClass = r?.verdict === 'qualifies' ? 'good' : r?.verdict === 'borderline' ? 'warn' : r?.verdict === 'short' ? 'bad' : '';

  return (
    <div className="stack-lg calc">
      <PageHead eyebrow="SBA 7(a) loan calculator" title="How much can your business borrow?">
        Pick your industry, type the figures from your last three tax returns, and see the estimate update as you go.
        Lenders treat tax returns as the source of truth, so start there.
      </PageHead>

      <section className="card stack">
        <h2 className="card-title">1. Your business</h2>
        <div className="calc-grid">
          <label className="field"><span>Industry</span>
            <select className="input" value={d.industryId} onChange={(e) => pickIndustry(e.target.value)}>
              <option value="">Choose…</option>
              {INDUSTRIES.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
            </select>
          </label>
          {industry && !industry.naics2 && (
            <label className="field"><span>Sector</span>
              <select className="input" value={d.sector} onChange={(e) => set('sector', e.target.value)}>
                <option value="">Choose…</option>
                {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
          )}
          <label className="field"><span>Situation</span>
            <select className="input" value={d.dealType} onChange={(e) => {
              const v = e.target.value as DealType;
              setD((c) => ({ ...c, dealType: v, addBackSalary: v === 'acquisition' }));
            }}>
              <option value="existing">I own the business</option>
              <option value="acquisition">I'm buying a business</option>
              <option value="startup">Starting up (no tax history)</option>
            </select>
          </label>
          <label className="field"><span>Loan is mainly for</span>
            <select className="input" value={d.use} onChange={(e) => set('use', e.target.value as UseOfProceeds)}>
              {(Object.keys(USE_LABELS) as UseOfProceeds[]).filter((u) => u !== 'soft-costs').map((u) => <option key={u} value={u}>{USE_LABELS[u]}</option>)}
            </select>
          </label>
          <label className="field"><span>State</span>
            <select className="input" value={d.state} onChange={(e) => set('state', e.target.value)}>
              <option value="">Choose…</option>
              {Object.keys(lenders?.states ?? {}).sort().map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
        {industry && (
          <p className="small muted" style={{ margin: 0 }}>
            Typical for {industry.label.toLowerCase()}: cost of goods {industry.benchmarks.cogsPct}%, operating expenses {industry.benchmarks.opexPct}%,
            operating margin {industry.benchmarks.ebitdaPct}% of revenue.{industry.referenceOnly && ' These figures come from a fast-growth model, so treat them as a reference only.'}
          </p>
        )}
      </section>

      <section className="card stack">
        <div className="spread">
          <h2 className="card-title">2. {d.dealType === 'startup' ? 'Your projection' : 'Figures from your tax returns'}</h2>
          {d.dealType !== 'startup' && (
            <div className="row">
              <button type="button" className="btn small" onClick={() => fileRef.current?.click()}>Upload returns / P&amp;L</button>
              <input ref={fileRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => void onFiles(e.target.files)} />
            </div>
          )}
        </div>
        {fileMsg && <p className="small callout" role="status" style={{ margin: 0 }}>{fileMsg}</p>}
        {d.dealType === 'startup' ? (
          <label className="field" style={{ maxWidth: 320 }}><span>Revenue you expect in a normal year</span>
            <input className="input" inputMode="decimal" placeholder="e.g. 450k" value={d.projectedRevenue} onChange={(e) => set('projectedRevenue', e.target.value)} />
          </label>
        ) : (
          <div className="table-scroll">
            <table className="table calc-table">
              <thead>
                <tr><th scope="col">Line</th>{COLS.map((c) => <th key={c} scope="col" className="r">{c === 'YTD' ? (
                  <span>YTD <select aria-label="Months year to date" className="input mini" value={d.ytdMonths} onChange={(e) => set('ytdMonths', Number(e.target.value))}>
                    {Array.from({ length: 11 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m} mo</option>)}
                  </select></span>) : c}</th>)}</tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.key}>
                    <th scope="row"><span>{row.label}</span><span className="faint small" style={{ display: 'block', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{row.hint}</span></th>
                    {COLS.map((col) => (
                      <td key={col} className="r">
                        <input className="input cell" inputMode="decimal" aria-label={`${row.label}, ${col}`}
                          value={d.grid[col]?.[row.key] ?? ''}
                          onChange={(e) => set('grid', { ...d.grid, [col]: { ...d.grid[col], [row.key]: e.target.value } })} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="small faint" style={{ margin: 0 }}>Type amounts like 250000, 250k or 1.2m. A year counts once net income is filled in.</p>
      </section>

      <section className="card stack">
        <h2 className="card-title">3. Loan and lender assumptions</h2>
        <div className="calc-grid">
          <label className="field"><span>Loan you want (optional)</span>
            <input className="input" inputMode="decimal" placeholder="e.g. 500k" value={d.requested} onChange={(e) => set('requested', e.target.value)} />
          </label>
          <label className="field"><span>Your cash going in</span>
            <input className="input" inputMode="decimal" placeholder="10% is typical" value={d.equity} onChange={(e) => set('equity', e.target.value)} />
          </label>
          <label className="field"><span>Lender coverage (DSCR)</span>
            <select className="input" value={d.dscr} onChange={(e) => set('dscr', Number(e.target.value))}>
              <option value={1.25}>1.25× — most SBA lenders</option>
              <option value={1.5}>1.50× — stricter</option>
              <option value={1.6}>1.60× — conservative</option>
            </select>
          </label>
          <label className="field"><span>Rate % {loan?.rate.isEstimate && <em className="faint">est. {loan.rate.pct}%</em>}</span>
            <input className="input" inputMode="decimal" placeholder={loan ? String(loan.rate.pct) : ''} value={d.rate} onChange={(e) => set('rate', e.target.value)} />
          </label>
          <label className="field"><span>Term (years)</span>
            <input className="input" inputMode="numeric" placeholder={loan ? String(loan.termYears) : ''} value={d.term} onChange={(e) => set('term', e.target.value)} />
          </label>
          <label className="field"><span>Payments on debt that stays (per year)</span>
            <input className="input" inputMode="decimal" value={d.existingDebt} onChange={(e) => set('existingDebt', e.target.value)} />
          </label>
        </div>
        <div className="calc-grid">
          <label className="row small"><input type="checkbox" checked={d.addBackSalary} onChange={(e) => set('addBackSalary', e.target.checked)} /> Add the owner's pay back (seller leaving, or you won't draw it)</label>
          {d.addBackSalary && (
            <label className="field"><span>Salary still needed (you or a manager)</span>
              <input className="input" inputMode="decimal" value={d.replacementSalary} onChange={(e) => set('replacementSalary', e.target.value)} />
            </label>
          )}
          <label className="field"><span>Household income outside the business</span>
            <input className="input" inputMode="decimal" placeholder="e.g. spouse's salary" value={d.outsideIncome} onChange={(e) => set('outsideIncome', e.target.value)} />
          </label>
        </div>
      </section>

      <section className="card stack calc-result" aria-live="polite">
        <h2 className="card-title">Your estimate</h2>
        {result.error && <p className="callout warn" style={{ margin: 0 }}>{result.error}</p>}
        {!r && !result.error && <p className="muted" style={{ margin: 0 }}>Fill in net income for at least one year{d.dealType === 'startup' ? ' — or your projected revenue' : ''} to see what the business can support.</p>}
        {r && loan && (
          <>
            <div className="calc-stats">
              <div className="stat"><span className="label">Cash flow supports up to</span><span className="big-number">{fmtMoney(r.maxLoan)}</span>
                <span className="small muted">at {loan.rate.pct}% over {loan.termYears} years, {d.dscr}× coverage{r.isProjection ? ' (projection)' : `, sized on ${r.limitingYear}`}</span></div>
              <div className="stat"><span className="label">Max monthly payment</span><span className="big-number">{fmtMoney(r.maxMonthlyPayment)}</span></div>
              {r.requestedLoan && (
                <div className="stat"><span className="label">Your {fmtMoney(r.requestedLoan)} loan</span>
                  <span className={`big-number ${verdictClass}`}>{r.verdict === 'qualifies' ? 'Fits' : r.verdict === 'borderline' ? 'Tight' : 'Short'}</span>
                  <span className="small muted">{fmtMoney(r.requestedMonthlyPayment!)} a month · weakest year {r.requestedMinDscr}×</span></div>
              )}
            </div>
            {r.growthMaxLoan && <p className="small callout" style={{ margin: 0 }}>Growth case: lenders that size on your latest year could go to {fmtMoney(r.growthMaxLoan)}.</p>}
            {r.sbaRule && (
              <p className="small callout" style={{ margin: 0 }}>
                <strong>{r.sbaRule.label}:</strong> {r.sbaRule.minDscr.toFixed(2)}× on the {r.sbaRule.basis} — supports up to {fmtMoney(r.sbaRule.maxLoan)}
                {r.sbaRule.requestedDscr !== null && <> · your loan: {r.sbaRule.requestedDscr}× <span className={r.sbaRule.meets ? 'good' : 'bad'}>{r.sbaRule.meets ? 'meets it' : 'falls short'}</span></>}.
                {' '}This is SBA's floor; most lenders size more conservatively, as above.
                <span className="faint" style={{ display: 'block' }}>{r.sbaRule.citation}{!r.sbaRule.verified && ' — from lender summaries of the SOP, not yet checked against its text'}.</span>
              </p>
            )}
            {!r.isProjection && (
              <div className="table-scroll">
                <table className="table">
                  <thead><tr><th>Year</th><th className="r">Cash for new debt</th><th className="r">Max loan</th>{r.requestedLoan && <th className="r">DSCR</th>}</tr></thead>
                  <tbody>{r.years.map((y) => (
                    <tr key={y.label} className={y.label === r.limitingYear ? 'limiting' : ''}>
                      <td>{y.label}{y.annualised && <span className="faint small"> (annualised)</span>}{y.label === r.limitingYear && <span className="badge" style={{ marginLeft: 8 }}>sets the limit</span>}</td>
                      <td className="r">{fmtMoney(y.availableForNewDebt)}</td><td className="r">{fmtMoney(y.maxLoan)}</td>
                      {r.requestedLoan && <td className={`r ${y.meetsTarget ? 'good' : 'bad'}`}>{y.dscr}×</td>}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {r.requestedLoan && (
              <p className="small" style={{ margin: 0 }}>Cash needed at closing: about <strong>{fmtMoney(loan.closing.cashAtClosingLikely)}</strong>, including your injection and the SBA guaranty fee.</p>
            )}
            {r.benchmarkFlags.length > 0 && <ul className="small stack" style={{ margin: 0 }}>{r.benchmarkFlags.map((f) => <li key={f.metric}>{f.note}</li>)}</ul>}
            <ul className="small faint" style={{ margin: 0 }}>{r.notes.map((n) => <li key={n}>{n}</li>)}</ul>
          </>
        )}
      </section>

      <section className="card stack">
        <div className="spread">
          <h2 className="card-title">Lenders active in your area</h2>
          <button type="button" className="btn primary small" onClick={() => setChatOpen(true)}>Ask the advisor</button>
        </div>
        {!d.state || !sector ? <p className="muted" style={{ margin: 0 }}>Choose your industry and state to see the SBA lenders making the most loans like yours.</p>
          : matches.length === 0 ? <p className="muted" style={{ margin: 0 }}>No lender data for that state yet. SBA's <a href="https://www.sba.gov/funding-programs/loans/lender-match" target="_blank" rel="noreferrer">Lender Match</a> can connect you.</p>
          : (
            <div className="table-scroll"><table className="table">
              <thead><tr><th>Lender</th><th className="r">SBA loans here</th><th className="r">Typical size</th><th className="r">Typical rate</th></tr></thead>
              <tbody>{matches.map((l) => (
                <tr key={l.name}><td>{l.name}<span className="faint small" style={{ display: 'block' }}>{l.city}, {l.hq}</span></td>
                  <td className="r">{l.loans}</td><td className="r">{fmtMoney(l.medianAmount)}</td><td className="r">{l.medianRatePct ? `${l.medianRatePct}%` : '—'}</td></tr>
              ))}</tbody>
            </table></div>
          )}
        <p className="small faint" style={{ margin: 0 }}>Ranked by SBA 7(a) approvals in your state since FY2022 (SBA FOIA data{lenders ? `, as of ${lenders.asOf}` : ''}). Activity is not an endorsement; Tyche is not paid by any lender.</p>
      </section>

      <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} summary={summary} />
    </div>
  );
}
